import store from '../redux/store';

/**
 * ML-20: users whose profile currency is INR read amounts in lakh/crore grouping (30,00,000).
 * Grouping is a reading convention, so it follows the signed-in user's profile currency unless a
 * currency is passed explicitly.
 */
const usesIndianGrouping = (currency) => {
  if (currency) return currency === 'INR';
  const user = store.getState()?.user?.data;
  return (user?.defaultCurrency || user?.currency) === 'INR';
};

/**
 * Insert digit-group separators into a plain integer string (an optional leading "-" is kept)
 * @param {string} intStr - e.g. "3000000"
 * @param {string} [currency] - pass to override the profile-based choice
 * @returns {string} "3,000,000" or, for INR users, "30,00,000"
 */
export const groupIntegerDigits = (intStr, currency) => {
  const str = String(intStr);
  const sign = str.startsWith('-') ? '-' : '';
  const digits = sign ? str.slice(1) : str;
  if (!usesIndianGrouping(currency) || digits.length <= 3) {
    return sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  const lastThree = digits.slice(-3);
  const rest = digits.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',');
  return `${sign}${rest},${lastThree}`;
};

/**
 * Format large numbers into readable financial units
 * @param {number|string} value - The number to format
 * @param {string} currency - Currency code (INR for Indian format, others for International)
 * @returns {string} The formatted number string
 * @example
 * formatLargeNumber(1250000, 'INR') => "12.5 L"
 * formatLargeNumber(25000000, 'INR') => "2.5 Cr"
 * formatLargeNumber(950000, 'USD') => "950K"
 * formatLargeNumber(1500000, 'USD') => "1.5 M"
 * formatLargeNumber(1500000000, 'USD') => "1.5 B"
 */
export const formatLargeNumber = (value, currency = 'USD') => {
  if (value === null || value === undefined || value === '') return '--';
  
  const num = parseFloat(value);
  
  // Handle invalid numbers, infinity, or extremely large numbers
  if (isNaN(num) || !Number.isFinite(num)) {
    return 'Data Error';
  }
  
  const absNum = Math.abs(num);
  const isNegative = num < 0;
  
  // Indian format for INR
  if (currency === 'INR') {
    if (absNum < 100000) {
      return formatNumberWithCommas(num);
    }
    if (absNum >= 10000000) { // 1 Crore (10,000,000+)
      return `${isNegative ? '-' : ''}${(absNum / 10000000).toFixed(1).replace('.0', '')} Cr`;
    } else { // 1 Lakh (100,000 to 9,999,999)
      return `${isNegative ? '-' : ''}${(absNum / 100000).toFixed(1).replace('.0', '')} L`;
    }
  }

  // Show normal formatting below 100,000 for international formats (mirrors the INR threshold)
  if (absNum < 100000) {
    return formatNumberWithCommas(num);
  }

  // 100,000 - 999,999: abbreviate to thousands so long values don't overflow
  // fixed-width UI (e.g. summary cards) the way INR's Lakh abbreviation does.
  if (absNum < 1000000) {
    return `${isNegative ? '-' : ''}${(absNum / 1000).toFixed(1).replace('.0', '')}K`;
  }

  // International format
  if (absNum >= 1e33) { // 1 Decillion
    return `${isNegative ? '-' : ''}${(absNum / 1e33).toFixed(1).replace('.0', '')} D`;
  } else if (absNum >= 1e30) { // 1 Nonillion
    return `${isNegative ? '-' : ''}${(absNum / 1e30).toFixed(1).replace('.0', '')} N`;
  } else if (absNum >= 1e27) { // 1 Octillion
    return `${isNegative ? '-' : ''}${(absNum / 1e27).toFixed(1).replace('.0', '')} O`;
  } else if (absNum >= 1e24) { // 1 Septillion
    return `${isNegative ? '-' : ''}${(absNum / 1e24).toFixed(1).replace('.0', '')} S`;
  } else if (absNum >= 1e21) { // 1 Sextillion
    return `${isNegative ? '-' : ''}${(absNum / 1e21).toFixed(1).replace('.0', '')} Sx`;
  } else if (absNum >= 1e18) { // 1 Quintillion
    return `${isNegative ? '-' : ''}${(absNum / 1e18).toFixed(1).replace('.0', '')} Q`;
  } else if (absNum >= 1e15) { // 1 Quadrillion
    return `${isNegative ? '-' : ''}${(absNum / 1e15).toFixed(1).replace('.0', '')} Qd`;
  } else if (absNum >= 1000000000000) { // 1 Trillion
    return `${isNegative ? '-' : ''}${(absNum / 1000000000000).toFixed(1).replace('.0', '')} T`;
  } else if (absNum >= 1000000000) { // 1 Billion
    return `${isNegative ? '-' : ''}${(absNum / 1000000000).toFixed(1).replace('.0', '')} B`;
  } else if (absNum >= 1000000) { // 1 Million (starts at 7 digits)
    return `${isNegative ? '-' : ''}${(absNum / 1000000).toFixed(1).replace('.0', '')} M`;
  }
  
  // Fallback for any remaining cases
  return formatNumberWithCommas(num);
};

/**
 * Format a number to include commas as thousands separators
 * Rounds to 2 decimal places for display
 * @param {number|string} value - The number to format
 * @returns {string} The formatted number string
 * @example
 * formatNumberWithCommas(150000) => "150,000"
 * formatNumberWithCommas("150000.50") => "150,000.50"
 * formatNumberWithCommas(472552.40640000004) => "472,552.41"
 */
export const formatNumberWithCommas = (value, currency) => {
  if (value === null || value === undefined || value === "") return "";
  
  const numValue = parseFloat(String(value).replace(/,/g, ""));
  if (isNaN(numValue)) return "";
  
  // Round to 2 decimal places
  const rounded = Math.round(numValue * 100) / 100;
  
  // Split into integer and decimal parts
  const parts = rounded.toFixed(2).split(".");
  const intPart = parts[0];
  const decPart = parts[1];
  
  // Add separators to integer part (lakh/crore grouping for INR users, ML-20)
  const formattedInteger = groupIntegerDigits(intPart, currency);
  
  // Remove trailing zeros from decimal part
  const trimmedDec = decPart.replace(/0+$/, "");
  
  // Return with decimal only if there are non-zero decimals
  return trimmedDec ? `${formattedInteger}.${trimmedDec}` : formattedInteger;
};

/**
 * Remove formatting (commas) from a number string
 * @param {string} value - The formatted number string
 * @returns {string|number} The unformatted number
 * @example
 * removeNumberFormatting("150,000.50") => "150000.50"
 */
export const removeNumberFormatting = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return value.toString().replace(/,/g, "");
};

/**
 * Process input value to allow only valid decimal numbers (max 2 decimal places)
 * Used for both onChange and onPaste handlers
 * @param {string} inputValue - The raw input value (from event or clipboard)
 * @returns {string} The sanitized value with max 2 decimal places
 * @example
 * processDecimalInput("500.784543") => "500.78"
 * processDecimalInput("1000.5") => "1000.5"
 * processDecimalInput("500") => "500"
 */
export const processDecimalInput = (inputValue) => {
  if (!inputValue) return "";

  // First remove commas (from formatted numbers), then remove other invalid characters
  let unformatted = inputValue.toString().replace(/,/g, "").replace(/[^\d.]/g, "");

  // Allow only one decimal point
  const parts = unformatted.split(".");
  if (parts.length > 2) {
    unformatted = parts[0] + "." + parts.slice(1).join("");
  }

  // Limit to 2 decimal places
  if (parts.length === 2 && parts[1].length > 2) {
    unformatted = parts[0] + "." + parts[1].slice(0, 2);
  }

  return unformatted;
};
